import { Badge, Code, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { reportLabel } from '../../../lib/reportLabel';
import { EntityRef } from '../../EntityRef';
import { DetailField, DetailFieldGrid } from '../DetailField';
import { recordDisplayValue } from './computedRecordTypes';
import { formatAddressLine, formatMoney, formatScalar } from './factTypes';

function compactJson(value: Record<string, unknown>): string {
    const text = JSON.stringify(value);
    return text.length > 120 ? `${text.slice(0, 117)}…` : text;
}

function fieldLabel(t: (key: string, options?: { defaultValue?: string }) => string, key: string) {
    return t(`factFields.${key}`, { defaultValue: key });
}

function ScalarFields({
    value,
    keys,
}: {
    value: Record<string, unknown>;
    keys: string[];
}) {
    const { t } = useTranslation();
    const empty = t('common.empty');
    const fields = keys
        .map((key) => ({ key, text: formatScalar(value[key]) }))
        .filter((entry) => entry.text);

    if (fields.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                {empty}
            </Text>
        );
    }

    return (
        <DetailFieldGrid>
            {fields.map(({ key, text }) => (
                <DetailField key={key} label={fieldLabel(t, key)} value={text ?? empty} />
            ))}
        </DetailFieldGrid>
    );
}

function LastFieldReportView({
    value,
    compact,
}: {
    value: Record<string, unknown>;
    compact?: boolean;
}) {
    const { t } = useTranslation();
    const empty = t('common.empty');

    if (compact) {
        const parts: ReactNode[] = [];
        if (typeof value.reportId === 'string') {
            parts.push(
                <EntityRef
                    key="report"
                    title={t('computedFacts.lastFieldReport')}
                    id={value.reportId}
                    href={`/reports?id=${value.reportId}`}
                    hideId
                />,
            );
        }
        if (typeof value.visitId === 'string') {
            parts.push(
                <EntityRef
                    key="visit"
                    title={t('entities.visit')}
                    id={value.visitId}
                    href={`/visits/${value.visitId}`}
                    hideId
                />,
            );
        }
        if (parts.length === 0) {
            return (
                <Text size="sm" c="dimmed">
                    {empty}
                </Text>
            );
        }
        return <Stack gap={4}>{parts}</Stack>;
    }

    return (
        <DetailFieldGrid>
            {typeof value.reportId === 'string' ? (
                <DetailField
                    label={t('entities.report')}
                    value={
                        <EntityRef
                            title={reportLabel({ id: value.reportId, workflowType: '' })}
                            id={value.reportId}
                            href={`/reports?id=${value.reportId}`}
                        />
                    }
                />
            ) : null}
            {typeof value.visitId === 'string' ? (
                <DetailField
                    label={t('entities.visit')}
                    value={
                        <EntityRef
                            title={t('entities.visit')}
                            id={value.visitId}
                            href={`/visits/${value.visitId}`}
                        />
                    }
                />
            ) : null}
            {formatScalar(value.company) ? (
                <DetailField label={fieldLabel(t, 'company')} value={formatScalar(value.company)} />
            ) : null}
            {formatScalar(value.loanId) ? (
                <DetailField label={fieldLabel(t, 'loanId')} value={formatScalar(value.loanId)} />
            ) : null}
        </DetailFieldGrid>
    );
}

function KnownLoanAmountView({
    value,
    compact,
}: {
    value: Record<string, unknown>;
    compact?: boolean;
}) {
    const { t } = useTranslation();
    const amount = formatMoney(value.amount, value.currency);

    if (compact) {
        return <Text size="sm">{amount ?? t('common.empty')}</Text>;
    }

    const sourceFields =
        value.sourceFields && typeof value.sourceFields === 'object' && !Array.isArray(value.sourceFields)
            ? (value.sourceFields as Record<string, unknown>)
            : null;

    return (
        <Stack gap="sm">
            <DetailFieldGrid>
                {amount ? <DetailField label={fieldLabel(t, 'amount')} value={amount} /> : null}
                {formatScalar(value.currency) ? (
                    <DetailField
                        label={fieldLabel(t, 'currency')}
                        value={formatScalar(value.currency)}
                    />
                ) : null}
            </DetailFieldGrid>
            {sourceFields && Object.keys(sourceFields).length > 0 ? (
                <Stack gap={4}>
                    <Text size="xs" tt="uppercase" fw={600} c="dimmed">
                        {fieldLabel(t, 'sourceFields')}
                    </Text>
                    <ScalarFields
                        value={sourceFields}
                        keys={['loanSize', 'price', 'amountRequested']}
                    />
                </Stack>
            ) : null}
        </Stack>
    );
}

function FinancialView({
    value,
    keys,
    compact,
}: {
    value: Record<string, unknown>;
    keys: string[];
    compact?: boolean;
}) {
    const { t } = useTranslation();

    if (compact) {
        const primary =
            formatMoney(value.outstandingBalance) ??
            formatMoney(value.amountOwed) ??
            formatMoney(value.loanAmount) ??
            formatMoney(value.loanSize) ??
            formatMoney(value.price) ??
            formatMoney(value.amount) ??
            formatMoney(value.totalPaid);
        return <Text size="sm">{primary ?? t('common.empty')}</Text>;
    }

    return (
        <DetailFieldGrid>
            {keys.map((key) => {
                const text = formatMoney(value[key]) ?? formatScalar(value[key]);
                if (!text) {
                    return null;
                }
                return <DetailField key={key} label={fieldLabel(t, key)} value={text} />;
            })}
        </DetailFieldGrid>
    );
}

function VerificationStatusView({
    value,
    compact,
}: {
    value: Record<string, unknown>;
    compact?: boolean;
}) {
    const { t } = useTranslation();
    const status = formatScalar(value.status);

    if (!status) {
        return (
            <Text size="sm" c="dimmed">
                {t('common.empty')}
            </Text>
        );
    }

    if (compact) {
        return (
            <Badge variant="light" size="sm">
                {status}
            </Badge>
        );
    }

    return (
        <DetailField
            label={fieldLabel(t, 'status')}
            value={
                <Badge variant="light" size="sm">
                    {status}
                </Badge>
            }
        />
    );
}

function ClaimedLocationView({
    value,
    compact,
}: {
    value: Record<string, unknown>;
    compact?: boolean;
}) {
    const { t } = useTranslation();

    if (compact) {
        const line = formatAddressLine(value);
        return <Text size="sm">{line ?? t('common.empty')}</Text>;
    }

    const lat = formatScalar(value.latitude);
    const lon = formatScalar(value.longitude);
    const coordinates = lat && lon ? `${lat}, ${lon}` : null;

    return (
        <DetailFieldGrid>
            {formatScalar(value.address) ? (
                <DetailField label={fieldLabel(t, 'address')} value={formatScalar(value.address)} />
            ) : null}
            {formatScalar(value.street) ? (
                <DetailField label={fieldLabel(t, 'street')} value={formatScalar(value.street)} />
            ) : null}
            {formatScalar(value.city) ? (
                <DetailField label={fieldLabel(t, 'city')} value={formatScalar(value.city)} />
            ) : null}
            {formatScalar(value.lga) ? (
                <DetailField label={fieldLabel(t, 'lga')} value={formatScalar(value.lga)} />
            ) : null}
            {formatScalar(value.state) ? (
                <DetailField label={fieldLabel(t, 'state')} value={formatScalar(value.state)} />
            ) : null}
            {formatScalar(value.landmark) ? (
                <DetailField label={fieldLabel(t, 'landmark')} value={formatScalar(value.landmark)} />
            ) : null}
            {coordinates ? (
                <DetailField label={fieldLabel(t, 'coordinates')} value={coordinates} />
            ) : null}
        </DetailFieldGrid>
    );
}

function JobOutcomeView({
    value,
    compact,
}: {
    value: Record<string, unknown>;
    compact?: boolean;
}) {
    const { t } = useTranslation();
    if (compact) {
        const summary =
            formatScalar(value.addressStatus) ??
            formatScalar(value.reasonForRejection) ??
            formatScalar(value.isCorrectAddress);
        return <Text size="sm">{summary ?? t('common.empty')}</Text>;
    }
    return (
        <ScalarFields
            value={value}
            keys={['reasonForRejection', 'addressStatus', 'isCorrectAddress']}
        />
    );
}

function GenericFactView({ value }: { value: Record<string, unknown> }) {
    const displayValue = recordDisplayValue(value);
    const keys = Object.keys(displayValue).sort();
    return <ScalarFields value={displayValue} keys={keys} />;
}

export function FactValueView({
    recordType,
    value,
    compact = false,
}: {
    recordType: string;
    value: Record<string, unknown>;
    compact?: boolean;
}) {
    const { t } = useTranslation();
    const displayValue = recordDisplayValue(value);

    if (recordType === 'last_field_report') {
        return <LastFieldReportView value={value} compact={compact} />;
    }

    if (typeof displayValue.reportId === 'string' && Object.keys(displayValue).length === 1) {
        return (
            <EntityRef
                title={reportLabel({ id: displayValue.reportId, workflowType: '' })}
                id={displayValue.reportId}
                href={`/reports?id=${displayValue.reportId}`}
            />
        );
    }

    if (typeof displayValue.visitId === 'string' && Object.keys(displayValue).length <= 2) {
        return (
            <EntityRef
                title={t('entities.visit')}
                id={displayValue.visitId}
                href={`/visits/${displayValue.visitId}`}
            />
        );
    }

    if (Object.keys(displayValue).length === 0) {
        return (
            <Text size="sm" c="dimmed">
                {t('common.empty')}
            </Text>
        );
    }

    switch (recordType) {
        case 'known_loan_amount':
        case 'subject.financial.loan':
            return <KnownLoanAmountView value={displayValue} compact={compact} />;
        case 'financial_obligations':
        case 'subject.financial.obligations':
            return (
                <FinancialView
                    value={displayValue}
                    keys={['amountOwed', 'outstandingBalance', 'totalPaid', 'unlockPrice']}
                    compact={compact}
                />
            );
        case 'financial_exposure':
            return (
                <FinancialView
                    value={displayValue}
                    keys={['loanAmount', 'amountOwed', 'outstandingBalance']}
                    compact={compact}
                />
            );
        case 'financial.summary':
            return (
                <FinancialView
                    value={displayValue}
                    keys={[
                        'amountOwed',
                        'outstandingBalance',
                        'totalPaid',
                        'unlockPrice',
                        'loanSize',
                        'price',
                    ]}
                    compact={compact}
                />
            );
        case 'contact_profile':
        case 'subject.contact':
            if (compact) {
                const summary =
                    formatScalar(displayValue.fullName) ??
                    formatScalar(displayValue.claimedBusinessName) ??
                    formatScalar(displayValue.primaryPhone);
                return <Text size="sm">{summary ?? t('common.empty')}</Text>;
            }
            return (
                <ScalarFields
                    value={displayValue}
                    keys={['fullName', 'claimedBusinessName', 'primaryPhone', 'alternativePhone']}
                />
            );
        case 'business_operations':
            return (
                <ScalarFields
                    value={displayValue}
                    keys={[
                        'availabilityHours',
                        'estimatedDailySales',
                        'slowestDayRevenue',
                        'shopSize',
                        'paymentCollectionMode',
                        'restockFrequency',
                        'estimatedMonthlyProfit',
                        'estimatedStockValue',
                    ]}
                />
            );
        case 'operational_profile':
        case 'operational.shop_profile':
            return (
                <ScalarFields
                    value={displayValue}
                    keys={[
                        'availabilityHours',
                        'estimatedDailySales',
                        'slowestDayRevenue',
                        'shopSize',
                        'paymentCollectionMode',
                        'restockFrequency',
                    ]}
                />
            );
        case 'operational.business_hours':
            return (
                <ScalarFields value={displayValue} keys={['availabilityHours']} />
            );
        case 'operational.sales_velocity':
            return (
                <ScalarFields
                    value={displayValue}
                    keys={['estimatedDailySales', 'slowestDayRevenue', 'estimatedMonthlyProfit']}
                />
            );
        case 'operational.payment_behavior':
            return (
                <ScalarFields
                    value={displayValue}
                    keys={['paymentFrequency', 'monthlyInstallments', 'paymentCollectionMode']}
                />
            );
        case 'claimed_location':
        case 'location.claimed':
            return <ClaimedLocationView value={displayValue} compact={compact} />;
        case 'verification_status':
            return <VerificationStatusView value={displayValue} compact={compact} />;
        case 'job.legacy_status':
            if (compact) {
                return <VerificationStatusView value={displayValue} compact={compact} />;
            }
            return (
                <ScalarFields value={displayValue} keys={['status', 'assignedAgent']} />
            );
        case 'claimed_assets':
        case 'asset.equipment':
            return (
                <ScalarFields
                    value={displayValue}
                    keys={['freezerType', 'freezerBrand', 'freezerSize', 'product']}
                />
            );
        case 'job.outcome':
            return <JobOutcomeView value={displayValue} compact={compact} />;
        case 'subject.business_profile':
            if (compact) {
                return (
                    <Text size="sm">
                        {formatScalar(displayValue.businessType) ?? t('common.empty')}
                    </Text>
                );
            }
            return (
                <ScalarFields
                    value={displayValue}
                    keys={[
                        'businessType',
                        'businessIndustry',
                        'businessSector',
                        'yearsInBusiness',
                        'meansOfId',
                        'hasBankAccountBvn',
                        'estimatedMonthlyProfit',
                        'estimatedStockValue',
                    ]}
                />
            );
        case 'visit.verification_summary':
            return (
                <ScalarFields
                    value={displayValue}
                    keys={['visitType', 'legacyStatus', 'isCorrectAddress', 'verifiedAt']}
                />
            );
        case 'job.intake.request':
            return (
                <ScalarFields
                    value={displayValue}
                    keys={['requestType', 'recoveryType', 'claimedPlace']}
                />
            );
        default:
            if (compact) {
                return (
                    <Code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} block>
                        {compactJson(displayValue)}
                    </Code>
                );
            }
            return <GenericFactView value={displayValue} />;
    }
}
